import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getTimezoneSettings, updateTimezoneSettings, getTimezoneOptions } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from '../ui/sonner';
import { 
  Clock, Globe, Save, Loader2, CheckCircle, AlertCircle, Settings
} from 'lucide-react';

const PengaturanPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentTimezone, setCurrentTimezone] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState('WIB');
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const [settings, options] = await Promise.all([
          getTimezoneSettings(),
          getTimezoneOptions()
        ]);
        
        setCurrentTimezone(settings);
        setSelectedTimezone(settings.timezone);
        setTimezoneOptions(options);
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error('Gagal memuat pengaturan');
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, []);

  // Format time for display
  const formatTimeForTimezone = (offset) => {
    const utc = currentTime.getTime() + (currentTime.getTimezoneOffset() * 60000);
    const localTime = new Date(utc + (offset * 3600000));
    return localTime.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false 
    });
  };

  // Handle save
  const handleSave = async () => {
    if (selectedTimezone === currentTimezone?.timezone) {
      toast.info('Tidak ada perubahan yang perlu disimpan');
      return;
    }

    try {
      setSaving(true);
      const result = await updateTimezoneSettings(selectedTimezone);
      
      setCurrentTimezone({
        timezone: result.timezone,
        timezone_label: result.timezone_label,
        utc_offset: result.utc_offset
      });
      
      toast.success(result.message, {
        description: `Zona waktu: ${result.timezone_label}`
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  // Check if user is admin
  if (user?.role !== 'admin_dinas') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Anda tidak memiliki akses ke halaman ini. Hanya Admin Dinas yang dapat mengubah pengaturan sistem.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg">
          <Settings className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Pengaturan Sistem</h1>
          <p className="text-gray-500">Kelola konfigurasi aplikasi SIGAP GURU</p>
        </div>
      </div>

      {/* Timezone Settings Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-lg">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-indigo-600" />
            <div>
              <CardTitle className="text-lg">Zona Waktu</CardTitle>
              <CardDescription>
                Pilih zona waktu yang sesuai dengan lokasi Anda
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Current Setting Info */}
          {currentTimezone && (
            <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700 mb-2">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Pengaturan Saat Ini</span>
              </div>
              <p className="text-emerald-800 font-semibold">
                {currentTimezone.timezone} - {currentTimezone.timezone_label}
              </p>
            </div>
          )}

          {/* Timezone Selection */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Pilih Zona Waktu Indonesia</Label>
            
            <RadioGroup 
              value={selectedTimezone} 
              onValueChange={setSelectedTimezone}
              className="space-y-3"
            >
              {timezoneOptions.map((option) => (
                <div
                  key={option.value}
                  data-testid={`timezone-option-${option.value.toLowerCase()}`}
                  className={`flex items-center space-x-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    selectedTimezone === option.value 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedTimezone(option.value)}
                >
                  <RadioGroupItem 
                    value={option.value} 
                    id={option.value}
                    className="text-indigo-600"
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor={option.value} 
                      className="text-base font-medium cursor-pointer"
                    >
                      {option.value}
                    </Label>
                    <p className="text-sm text-gray-500">{option.label}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-indigo-600">
                      <Clock className="w-4 h-4" />
                      <span className="font-mono text-lg font-semibold">
                        {formatTimeForTimezone(option.utc_offset)}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">
                      UTC+{option.utc_offset}
                    </span>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Info Box */}
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Penting:</strong> Perubahan zona waktu akan mempengaruhi:
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Perhitungan status absensi (Tepat Waktu / Terlambat)</li>
                <li>Watermark waktu pada foto absensi</li>
                <li>Jadwal pengingat notifikasi</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave}
              disabled={saving || selectedTimezone === currentTimezone?.timezone}
              data-testid="save-timezone-btn"
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Simpan Pengaturan
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Additional Info */}
      <Card className="border-0 shadow-md bg-gradient-to-r from-slate-50 to-gray-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-700 mb-3">Tentang Zona Waktu Indonesia</h3>
          <div className="grid gap-4 text-sm text-gray-600">
            <div className="flex gap-3">
              <div className="w-16 font-semibold text-indigo-600">WIB</div>
              <div>Waktu Indonesia Barat (UTC+7) - Sumatra, Jawa, Kalimantan Barat & Tengah</div>
            </div>
            <div className="flex gap-3">
              <div className="w-16 font-semibold text-purple-600">WITA</div>
              <div>Waktu Indonesia Tengah (UTC+8) - Kalimantan Timur & Selatan, Sulawesi, Bali, NTB, NTT</div>
            </div>
            <div className="flex gap-3">
              <div className="w-16 font-semibold text-pink-600">WIT</div>
              <div>Waktu Indonesia Timur (UTC+9) - Maluku, Papua</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PengaturanPage;
